import { defaultRulePattern, isIncomingSwish } from '@budgie/shared';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { EmptyState } from '../../components/common/EmptyState';
import { CardSkeleton } from '../../components/common/Skeleton';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { trpc } from '../../lib/trpc';
import { usePageTitle } from '../../lib/usePageTitle';

export const Route = createFileRoute('/activity/')({
  component: ActivityPage,
});

function formatAmount(amount: number) {
  const kr = Math.abs(amount) / 100;
  const formatted = kr.toLocaleString('sv-SE', {
    minimumFractionDigits: amount % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `${amount < 0 ? '-' : ''}${formatted} kr`;
}

function formatDate(d: string | Date | null | undefined) {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleDateString('sv-SE');
}

function formatTime(d: string | Date | null | undefined) {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleString('sv-SE', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const CARD = 'rounded-2xl border border-border bg-card';

type Tab = 'flagged' | 'history' | 'accounts';

function ActivityPage() {
  usePageTitle('Synkaktivitet');
  const [tab, setTab] = useState<Tab>('flagged');

  const flaggedQuery = trpc.sync.flagged.useQuery({ limit: 50 });
  const accountsQuery = trpc.sync.linkedAccounts.useQuery();
  const sessionsQuery = trpc.enableBanking.sessions.useQuery();

  const flaggedCount = flaggedQuery.data?.length ?? 0;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Synkaktivitet</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Vad bankkopplingen har hämtat och vad som behöver en blick.
          </p>
        </div>
        <SyncButton />
      </header>

      {/* Tabs */}
      <div role="tablist" className="flex gap-1 rounded-xl bg-muted p-1">
        <TabButton active={tab === 'flagged'} onClick={() => setTab('flagged')}>
          Att granska
          {flaggedCount > 0 && (
            <span className="ml-1.5 rounded-full bg-warning-soft px-1.5 text-xs font-semibold text-warning tabular-nums">
              {flaggedCount}
            </span>
          )}
        </TabButton>
        <TabButton active={tab === 'history'} onClick={() => setTab('history')}>
          Synklogg
        </TabButton>
        <TabButton active={tab === 'accounts'} onClick={() => setTab('accounts')}>
          Konton
        </TabButton>
      </div>

      {tab === 'flagged' && <FlaggedTab />}
      {tab === 'history' && <HistoryTab />}
      {tab === 'accounts' && (
        <AccountsTab
          accounts={accountsQuery.data ?? []}
          sessions={(sessionsQuery.data ?? []).map((s) => ({
            id: s.id,
            aspspName: s.aspspName,
            accounts: (s.accounts ?? []).map((a: Record<string, unknown>) => ({
              uid: a.uid as string | undefined,
              accountId: a.account_id as { iban?: string } | undefined,
              name: a.name as string | undefined,
            })),
          }))}
          isLoading={accountsQuery.isLoading}
        />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'flex flex-1 items-center justify-center rounded-lg px-2 py-1.5 text-sm font-medium whitespace-nowrap transition-colors max-sm:min-h-10 sm:flex-none sm:px-3',
        active
          ? 'bg-card text-foreground shadow-xs'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

// ── Sync Button ───────────────────────────────────────────────────────────────

function SyncButton() {
  const utils = trpc.useUtils();
  const syncMutation = trpc.sync.run.useMutation({
    onSuccess: (data) => {
      const totalNew = data.results.reduce((s: number, r: { newTxns: number }) => s + r.newTxns, 0);
      if (totalNew > 0) {
        toast.success(`${totalNew} nya transaktioner synkade`);
      } else {
        toast.info('Inga nya transaktioner');
      }
      void utils.sync.flagged.invalidate();
      void utils.sync.logs.invalidate();
    },
    onError: () => {
      toast.error('Synkfel — försök igen');
    },
  });

  return (
    <Button onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending} size="sm">
      {syncMutation.isPending ? 'Synkar…' : 'Synka nu'}
    </Button>
  );
}

// ── Flagged Transactions Tab ──────────────────────────────────────────────────

function FlaggedTab() {
  const flaggedQuery = trpc.sync.flagged.useQuery({ limit: 50 });
  const categoriesQuery = trpc.category.listGroups.useQuery();
  const utils = trpc.useUtils();
  const resolveMutation = trpc.sync.resolve.useMutation({
    onSuccess: () => {
      void utils.sync.flagged.invalidate();
    },
  });
  const dismissAllMutation = trpc.sync.dismissAll.useMutation({
    onSuccess: (data) => {
      toast.success(`${data.dismissed} transaktioner godkända`);
      void utils.sync.flagged.invalidate();
    },
  });

  if (flaggedQuery.isLoading) return <CardSkeleton lines={5} />;

  const flagged = flaggedQuery.data ?? [];
  if (flagged.length === 0) {
    return (
      <EmptyState
        icon="✅"
        title="Inga transaktioner att granska"
        description="Alla synkade transaktioner har hanterats."
      />
    );
  }

  const allCategories =
    categoriesQuery.data?.flatMap((g) =>
      g.categories.map((c: { id: string; name: string }) => ({
        id: c.id,
        name: c.name,
        groupName: g.name,
      })),
    ) ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {flagged.length} transaktion
          {flagged.length !== 1 ? 'er' : ''} behöver granskas
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => dismissAllMutation.mutate()}
          disabled={dismissAllMutation.isPending}
        >
          Godkänn alla
        </Button>
      </div>

      {flagged.map((txn) => (
        <FlaggedTransactionRow
          key={txn.id}
          txn={txn}
          categories={allCategories}
          onResolve={(categoryId, createRule, rulePattern) => {
            resolveMutation.mutate({
              transactionId: txn.id,
              categoryId,
              createRule,
              rulePattern,
            });
          }}
          isResolving={resolveMutation.isPending}
        />
      ))}
    </div>
  );
}

type FlaggedTxn = {
  id: string;
  date: string;
  description: string;
  amount: number;
  categoryId: string | null;
  categoryName: string | null;
  accountName: string | null;
};

function FlaggedTransactionRow({
  txn,
  categories,
  onResolve,
  isResolving,
}: {
  txn: FlaggedTxn;
  categories: { id: string; name: string; groupName: string }[];
  onResolve: (categoryId: string | null, createRule: boolean, rulePattern?: string) => void;
  isResolving: boolean;
}) {
  const [selectedCategory, setSelectedCategory] = useState(txn.categoryId ?? '');
  const [createRule, setCreateRule] = useState(false);
  const [rulePattern, setRulePattern] = useState(() => defaultRulePattern(txn.description));

  return (
    <div className={cn(CARD, 'p-4')}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3 sm:justify-start">
            <span className="truncate text-sm font-medium">{txn.description}</span>
            <span
              className={cn(
                'shrink-0 text-sm font-semibold tabular-nums',
                txn.amount < 0 ? 'text-foreground' : 'text-positive',
              )}
            >
              {formatAmount(txn.amount)}
            </span>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span className="tabular-nums">{formatDate(txn.date)}</span>
            {txn.accountName && <span>· {txn.accountName}</span>}
            {txn.categoryName && (
              <Badge variant="secondary" className="text-xs">
                {txn.categoryName}
              </Badge>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="h-8 w-full text-xs sm:w-48">
              <SelectValue placeholder="Välj kategori…" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id} className="text-xs">
                  {c.groupName} / {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            size="sm"
            disabled={!selectedCategory || isResolving}
            onClick={() => onResolve(selectedCategory || null, createRule, rulePattern)}
          >
            Spara
          </Button>
        </div>
      </div>

      {/* Create rule option (never for incoming Swish: each is sorted by hand) */}
      {!isIncomingSwish(txn.description, txn.amount) && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Checkbox
            id={`rule-${txn.id}`}
            checked={createRule}
            onCheckedChange={(v) => setCreateRule(v === true)}
          />
          <label
            htmlFor={`rule-${txn.id}`}
            className="cursor-pointer max-sm:-my-3 max-sm:py-3 max-sm:pr-3"
          >
            Skapa regel
          </label>
          {createRule && (
            <Input
              value={rulePattern}
              onChange={(e) => setRulePattern(e.target.value)}
              className="h-7 w-full text-xs sm:w-48"
              placeholder="Mönster…"
            />
          )}
        </div>
      )}
    </div>
  );
}

// ── Sync History Tab ──────────────────────────────────────────────────────────

function HistoryTab() {
  const logsQuery = trpc.sync.logs.useQuery({ limit: 20 });
  const [expandedLog, setExpandedLog] = useState<string | null>(null);

  if (logsQuery.isLoading) return <CardSkeleton lines={4} />;

  const logs = logsQuery.data ?? [];
  if (logs.length === 0) {
    return (
      <EmptyState
        icon="📋"
        title="Ingen synkhistorik"
        description="Anslut ett bankkonto och kör en synk för att se historik."
      />
    );
  }

  return (
    <div className="space-y-2">
      {logs.map((log) => (
        <SyncLogRow
          key={log.id}
          log={log}
          isExpanded={expandedLog === log.id}
          onToggle={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
        />
      ))}
    </div>
  );
}

type SyncLogItem = {
  id: string;
  bankAccountName: string | null;
  status: string;
  startedAt: Date | string;
  completedAt: Date | string | null;
  txnFetched: number;
  txnNew: number;
  txnDuplicate: number;
  txnAutoAssigned: number;
  txnFlagged: number;
  error?: unknown;
};

function SyncLogRow({
  log,
  isExpanded,
  onToggle,
}: {
  log: SyncLogItem;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className={cn(CARD, 'overflow-hidden')}>
      <button
        type="button"
        className="w-full px-4 py-3 text-left transition-colors hover:bg-muted/40"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm font-medium">
              {log.bankAccountName ?? 'Okänt konto'}
            </span>
            <SyncStatusBadge status={log.status} />
          </div>
          <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
            {formatTime(log.startedAt)}
            <ChevronDown
              className={cn('size-3.5 transition-transform', isExpanded && 'rotate-180')}
            />
          </span>
        </div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground tabular-nums">
          <span>Hämtade: {log.txnFetched}</span>
          <span>Nya: {log.txnNew}</span>
          <span>Auto: {log.txnAutoAssigned}</span>
          <span>Flaggade: {log.txnFlagged}</span>
          <span>Dubblett: {log.txnDuplicate}</span>
        </div>
      </button>

      {isExpanded && <SyncLogDetail syncLogId={log.id} />}
    </div>
  );
}

function SyncStatusBadge({ status }: { status: string }) {
  if (status === 'completed')
    return <Badge className="bg-positive-soft text-xs text-positive">Klar</Badge>;
  if (status === 'failed')
    return <Badge className="bg-negative-soft text-xs text-negative">Misslyckad</Badge>;
  return (
    <Badge variant="secondary" className="text-xs">
      Kör…
    </Badge>
  );
}

function SyncLogDetail({ syncLogId }: { syncLogId: string }) {
  const detailQuery = trpc.sync.logDetail.useQuery({ syncLogId });

  if (detailQuery.isLoading) {
    return <p className="px-4 pb-3 text-xs text-muted-foreground">Laddar…</p>;
  }

  const entries = detailQuery.data?.entries ?? [];
  if (entries.length === 0) {
    return <p className="px-4 pb-3 text-xs text-muted-foreground">Inga loggposter</p>;
  }

  return (
    <div className="px-4 pb-4">
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-xs">
          <thead className="bg-muted/60">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium text-muted-foreground">Datum</th>
              <th className="px-2 py-1.5 text-left font-medium text-muted-foreground">
                Beskrivning
              </th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Belopp</th>
              <th className="px-2 py-1.5 text-left font-medium text-muted-foreground">Åtgärd</th>
              <th className="px-2 py-1.5 text-left font-medium text-muted-foreground">Detalj</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td className="px-2 py-1.5 whitespace-nowrap text-muted-foreground tabular-nums">
                  {formatDate(entry.txnDate)}
                </td>
                <td className="max-w-[200px] truncate px-2 py-1.5">{entry.txnDescription}</td>
                <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums">
                  {entry.txnAmount != null ? formatAmount(entry.txnAmount) : '—'}
                </td>
                <td className="px-2 py-1.5">
                  <ActionBadge action={entry.action} />
                </td>
                <td className="max-w-[200px] truncate px-2 py-1.5 text-muted-foreground">
                  {entry.categoryName
                    ? `${entry.categoryName}${entry.rulePattern ? ` (${entry.rulePattern})` : ''}${entry.confidence != null ? ` ${entry.confidence}%` : ''}`
                    : (entry.detail ?? '—')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const ACTION_LABELS: Record<string, { label: string; className: string }> = {
  autoCategorized: { label: 'Auto', className: 'bg-positive-soft text-positive' },
  flagged: { label: 'Flaggad', className: 'bg-warning-soft text-warning' },
  transfer: { label: 'Överföring', className: 'bg-muted text-muted-foreground' },
  billMatched: { label: 'Räkning', className: 'border-border text-foreground' },
  incomeMatched: { label: 'Inkomst', className: 'border-border text-foreground' },
  loanMatched: { label: 'Lån', className: 'border-border text-foreground' },
  duplicate: { label: 'Dubblett', className: 'bg-muted text-muted-foreground' },
};

function ActionBadge({ action }: { action: string }) {
  // Convert DB snake_case to camelCase for lookup
  const key = action.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  const info = ACTION_LABELS[key] ?? {
    label: action,
    className: 'bg-muted text-muted-foreground',
  };
  return <Badge className={cn('bg-transparent text-[10px]', info.className)}>{info.label}</Badge>;
}

// ── Accounts Tab ──────────────────────────────────────────────────────────────

type BankAccountInfo = {
  id: string;
  friendlyName: string | null;
  accountNumber: string;
  iban: string | null;
  ebAccountUid: string | null;
  isActive: boolean;
};

type SessionInfo = {
  id: string;
  aspspName: string;
  accounts: Array<{
    uid?: string | undefined;
    accountId?: { iban?: string } | undefined;
    name?: string | undefined;
  }>;
};

function AccountsTab({
  accounts,
  sessions,
  isLoading,
}: {
  accounts: BankAccountInfo[];
  sessions: SessionInfo[];
  isLoading: boolean;
}) {
  const utils = trpc.useUtils();
  const linkMutation = trpc.sync.linkAccount.useMutation({
    onSuccess: () => {
      toast.success('Konto kopplat');
      void utils.sync.linkedAccounts.invalidate();
    },
    onError: () => {
      toast.error('Kunde inte koppla konto');
    },
  });

  if (isLoading) return <CardSkeleton lines={3} />;

  // Flatten all EB session accounts
  const ebAccounts = sessions.flatMap((s) =>
    (s.accounts ?? []).map((a) => ({
      uid: a.uid ?? '',
      iban: a.accountId?.iban ?? '',
      name: a.name ?? a.accountId?.iban ?? a.uid ?? 'Okänt',
      aspsp: s.aspspName,
    })),
  );

  if (accounts.length === 0) {
    return (
      <EmptyState
        icon="🏦"
        title="Inga bankkonton"
        description="Importera transaktioner för att skapa bankkonton, sedan koppla dem till bankens API."
      />
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Koppla varje bankkonto till ett konto från bankens API för att aktivera bakgrundssynk.
      </p>

      <ul className={cn(CARD, 'divide-y divide-border')}>
        {accounts.map((acct) => (
          <li
            key={acct.id}
            className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">
                {acct.friendlyName ?? acct.accountNumber}
              </div>
              {acct.iban && (
                <div className="truncate text-xs text-muted-foreground tabular-nums">
                  {acct.iban}
                </div>
              )}
            </div>

            {acct.ebAccountUid ? (
              <Badge className="bg-positive-soft text-xs text-positive">Kopplad</Badge>
            ) : (
              <Select
                onValueChange={(uid) => {
                  linkMutation.mutate({
                    bankAccountId: acct.id,
                    ebAccountUid: uid,
                  });
                }}
              >
                <SelectTrigger className="h-8 w-full text-xs sm:w-56">
                  <SelectValue placeholder="Välj bankkonto…" />
                </SelectTrigger>
                <SelectContent>
                  {ebAccounts.map((eb) => (
                    <SelectItem key={eb.uid} value={eb.uid} className="text-xs">
                      {eb.aspsp} — {eb.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
